import { createAsyncThunk } from '@reduxjs/toolkit';

import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ApiPaths } from '../../../api/APIPaths';
import { ILessonTutorFeedback, ILessonTutorFeedbackRewardData } from '.';

export type TCreateLessonTutorFeedback = {
  section: string;
  students: string[];
  isStudentAttended: 'Yes' | 'No';
  lessonExtension?: number;
  lessonExtensionReason?: string;
  studentAttentiveness?: string;
  graspingAndApplicationOfConceptTaught?: string;
  progressForThisTopicAndSyllabus?: string;
  homeworkCompletedFromPreviousLesson?: string;
  homeworkSetForThisLesson?: 'Yes' | 'No';
  quizScore?: number;
  quizScoreOutOf?: number;
  overallStudentRating?: number;
  feedbackRewards?: string[];
  comments?: string;
};

export type TGetLessonTutorFeedbacks = {
  limit: number;
  offset: number;
  section?: string;
  tutorId?: string;
  studentId?: string;
};

export type TUpdateLessonTutorFeedback = {
  id: string;
  isStudentAttended: 'Yes' | 'No';
  lessonExtension?: number;
  lessonExtensionReason?: string;
  studentAttentiveness?: string;
  graspingAndApplicationOfConceptTaught?: string;
  progressForThisTopicAndSyllabus?: string;
  homeworkCompletedFromPreviousLesson?: string;
  homeworkSetForThisLesson?: 'Yes' | 'No';
  quizScore?: number;
  quizScoreOutOf?: number;
  overallStudentRating?: number;
  feedbackRewards?: string[];
  comments?: string;
};

const apiDataProvider = new APIDataProvider();

export const getLessonTutorFeedbackRewards = createAsyncThunk(
  '/lesson-tutor-feedbacks/feedback-rewards',
  async () => {
    const path = `${ApiPaths.lessonTutorFeedbacks}/feedback-rewards`;

    const response = await apiDataProvider.get<
      TApiResponse<ILessonTutorFeedbackRewardData>
    >(path);

    return response.data;
  }
);

export const createLessonTutorFeedback = createAsyncThunk(
  '/lesson-tutor-feedbacks/create',
  async (data: TCreateLessonTutorFeedback) => {
    const path = `${ApiPaths.lessonTutorFeedbacks}`;

    const response = await apiDataProvider.post<
      TApiResponse<ILessonTutorFeedback[]>
    >(path, data);

    return response.data;
  }
);

export const getLessonTutorFeedbacks = createAsyncThunk(
  '/lesson-tutor-feedbacks/list',
  async ({
    limit,
    offset,
    section,
    tutorId,
    studentId,
  }: TGetLessonTutorFeedbacks) => {
    let path = `${ApiPaths.lessonTutorFeedbacks}?limit=${limit}&offset=${offset}`;

    if (section) {
      path += `&section=${section}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ILessonTutorFeedback>>
    >(path);

    return response.data;
  }
);

export const updateLessonTutorFeedbacks = createAsyncThunk(
  '/lesson-tutor-feedbacks/update-one',
  async (data: TUpdateLessonTutorFeedback) => {
    const path = `${ApiPaths.lessonTutorFeedbacks}/${data.id}`;

    const response = await apiDataProvider.put<
      TApiResponse<ILessonTutorFeedback>
    >(path, data);

    return response.data;
  }
);
